import { Form, Formik, FormikComputedProps, FormikErrors } from 'formik';
import { ReactNode } from 'react';

import { UserRole } from '../../../types/UserRole';
import { FormikInput } from '../../cdl/Input/FormikInput';
import { SegmentedControl, SegmentedControlOption } from '../../cdl/SegmentedControl/SegmentedControl';
import { Text } from '../../cdl/Text/Text';
import { FormikCustomerCompanyMultiSelect } from '../../common/form-elements/formik/FormikCustomerCompanyMultiSelect';
import { FormikSupplierCompanyMultiSelect } from '../../common/form-elements/formik/FormikSupplierCompanyMultiSelect';
import { translate } from '../../common/helpers/translate.helper';
import { useEnums } from '../../common/hooks/useEnums';
import { Box } from '../../common/ui/Box';

export interface SsoGroupFormValues {
    groupName: string;
    role?: UserRole;
    companyIds: string[];
}

type RoleOption =
    | UserRole.GROUP_ADMIN
    | UserRole.COMPANY_ADMIN
    | UserRole.COMPANY_EMPLOYEE
    | UserRole.COMPANY_INSPECTOR;

interface SsoGroupFormProps {
    groupId: string;
    groupType: 'customer' | 'supplier';
    initialValues: SsoGroupFormValues;
    onSubmit: (values: SsoGroupFormValues, helpers: { resetForm: () => void }) => Promise<void>;
    validate: (values: SsoGroupFormValues) => FormikErrors<SsoGroupFormValues>;
    children: (formikState: Pick<FormikComputedProps<SsoGroupFormValues>, 'isValid' | 'dirty'>) => ReactNode;
    resetKey?: number;
    enableReinitialize?: boolean;
}

export const SsoGroupForm = ({
    groupId,
    groupType,
    initialValues,
    onSubmit,
    validate,
    children,
    resetKey = 0,
    enableReinitialize = false,
}: SsoGroupFormProps) => {
    const { getHumanReadableValue } = useEnums();

    const CompanySelect =
        groupType === 'customer' ? FormikCustomerCompanyMultiSelect : FormikSupplierCompanyMultiSelect;

    const roleOptions: SegmentedControlOption<RoleOption>[] = [
        { label: getHumanReadableValue('role', UserRole.GROUP_ADMIN), value: UserRole.GROUP_ADMIN },
        { label: getHumanReadableValue('role', UserRole.COMPANY_ADMIN), value: UserRole.COMPANY_ADMIN },
        { label: getHumanReadableValue('role', UserRole.COMPANY_EMPLOYEE), value: UserRole.COMPANY_EMPLOYEE },
        { label: getHumanReadableValue('role', UserRole.COMPANY_INSPECTOR), value: UserRole.COMPANY_INSPECTOR },
    ];

    return (
        <Formik
            initialValues={initialValues}
            validate={validate}
            onSubmit={onSubmit}
            validateOnChange
            validateOnBlur
            enableReinitialize={enableReinitialize}
        >
            {(formikContext) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap={24}>
                        <Box display="flex" flexDirection="column" gap={8}>
                            <FormikInput
                                name="groupName"
                                label={translate('sso.groupNameLabel')}
                                caption={translate('sso.groupNameHelperText')}
                            />
                        </Box>

                        <Box display="flex" flexDirection="column" gap={8}>
                            <Text variant="small" as="label">
                                {translate('sso.roleLabel')}
                            </Text>
                            <SegmentedControl<RoleOption>
                                key={resetKey}
                                options={roleOptions}
                                value={formikContext.values.role as RoleOption}
                                onChange={(value: RoleOption) => {
                                    formikContext.setFieldValue('role', value);
                                }}
                            />
                            {formikContext.touched.role && formikContext.errors.role ? (
                                <Text variant="extraSmall" color="foreground.critical">
                                    {formikContext.errors.role}
                                </Text>
                            ) : null}
                        </Box>

                        {formikContext.values.role && formikContext.values.role !== UserRole.GROUP_ADMIN ? (
                            <CompanySelect
                                name="companyIds"
                                groupId={groupId}
                                label={translate('sso.companiesLabel')}
                                placeholder={translate('sso.companiesPlaceholder')}
                            />
                        ) : null}

                        {children({ isValid: formikContext.isValid, dirty: formikContext.dirty })}
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
